import type { FC } from 'react'
import { useMemo } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box } from '@mui/material'
import { EventType } from '@/features/events/shared/types'
import { FileInfo, ToolCodeBlock, ToolHeader } from '../shared'
import { calculateWriteStats, type DiffStats } from '@/features/events/shared/utils'
import { getLanguageFromPath } from '../utils/fileModificationUtils'
import { LazyCollapsibleContent } from './shared'

interface WriteContextRendererProps {
  readonly file_path: string
  readonly content: string
}

interface WriteToolContentProps {
  readonly content: string
  readonly language: string
  readonly showLineNumbers: boolean
}

interface WriteContextData {
  readonly language: string
  readonly stats: DiffStats
}

export const WriteContextRenderer: FC<WriteContextRendererProps> = ({ file_path, content }) => {
  const { language, stats } = useWriteContextData(file_path, content)

  return (
    <>
      <ToolHeader eventType={EventType.TOOL_PERMISSION_REQUEST} toolName="Write" />
      <FileInfo filePath={file_path} stats={stats} />

      <WriteToolContent content={content} language={language} showLineNumbers={false} />
    </>
  )
}

function useWriteContextData(file_path: string, content: string): WriteContextData {
  const language = useMemo(() => getLanguageFromPath(file_path), [file_path])

  const stats = useMemo(() => calculateWriteStats(content), [content])

  return { language, stats }
}

const WriteToolContent: FC<WriteToolContentProps> = ({ content, language, showLineNumbers }) => {
  return (
    <Box sx={contentContainerSx}>
      <LazyCollapsibleContent content={content}>
        {(displayContent) => (
          <ToolCodeBlock
            code={displayContent}
            language={language}
            showLineNumbers={showLineNumbers}
            diffPrefix="+"
          />
        )}
      </LazyCollapsibleContent>
    </Box>
  )
}

const contentContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
}
